import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Clock, Wallet, TrendingUp, Sparkles } from 'lucide-react';

const slides = [
    {
        icon: Users,
        headline: 'Employee Management',
        description: 'Centralized profiles, documents, and org charts for your entire workforce.',
        gradient: 'from-yellow-500 to-amber-500',
        glow: 'rgba(234,179,8,0.2)',
        accent: '#eab308',
        particles: ['#eab308', '#f59e0b', '#fbbf24', '#d97706']
    },
    {
        icon: Clock,
        headline: 'Attendance & Leaves',
        description: 'Automated time tracking, shift scheduling, and leave management.',
        gradient: 'from-yellow-500 to-orange-400',
        glow: 'rgba(251,146,60,0.2)',
        accent: '#fb923c',
        particles: ['#fb923c', '#f97316', '#fdba74', '#ea580c']
    },
    {
        icon: Wallet,
        headline: 'Payroll & Compliance',
        description: 'Salary processing, PF, TDS, and payslip generation — all automated.',
        gradient: 'from-yellow-500 to-amber-600',
        glow: 'rgba(217,119,6,0.2)',
        accent: '#d97706',
        particles: ['#d97706', '#f59e0b', '#b45309', '#fbbf24']
    },
    {
        icon: TrendingUp,
        headline: 'Performance Reviews',
        description: '360° reviews, goal tracking, and AI-powered performance insights.',
        gradient: 'from-yellow-400 to-yellow-600',
        glow: 'rgba(250,204,21,0.2)',
        accent: '#facc15',
        particles: ['#facc15', '#eab308', '#fde047', '#ca8a04']
    },
    {
        icon: Sparkles,
        headline: 'AI-Powered HR',
        description: 'Intelligent HR assistant that answers queries and automates workflows.',
        gradient: 'from-amber-400 to-yellow-500',
        glow: 'rgba(251,191,36,0.2)',
        accent: '#fbbf24',
        particles: ['#fbbf24', '#f59e0b', '#fde68a', '#d97706']
    }
];

const SLIDE_DURATION = 5000;

// Floating particle component
const FloatingParticle = ({ color, delay, size, x, y, duration }) => (
    <motion.div
        className="absolute rounded-full"
        style={{ width: size, height: size, backgroundColor: color, left: `${x}%`, top: `${y}%` }}
        initial={{ opacity: 0, scale: 0 }}
        animate={{
            opacity: [0, 0.6, 0.3, 0],
            scale: [0, 1, 1.2, 0],
            y: [0, -40, -80, -120],
            x: [0, (Math.random() - 0.5) * 60]
        }}
        transition={{ duration, delay, repeat: Infinity, repeatDelay: Math.random() * 2 }}
    />
);

// Orbiting ring around icon
const OrbitRing = ({ size, duration, delay, color, dashed }) => (
    <motion.div
        className="absolute rounded-full border"
        style={{
            width: size,
            height: size,
            top: '50%',
            left: '50%',
            marginTop: -size / 2,
            marginLeft: -size / 2,
            borderColor: color,
            borderStyle: dashed ? 'dashed' : 'solid',
            opacity: 0.15
        }}
        animate={{ rotate: 360 }}
        transition={{ duration, delay, repeat: Infinity, ease: 'linear' }}
    />
);

const FeatureShowcase = () => {
    const [current, setCurrent] = useState(0);
    const [progress, setProgress] = useState(0);

    const goToSlide = useCallback((index) => {
        setCurrent(index);
        setProgress(0);
    }, []);

    // Auto-advance
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrent(prev => (prev + 1) % slides.length);
            setProgress(0);
        }, SLIDE_DURATION);
        return () => clearInterval(timer);
    }, [current]);

    // Progress bar animation
    useEffect(() => {
        setProgress(0);
        const start = Date.now();
        const frame = () => {
            const elapsed = Date.now() - start;
            const pct = Math.min(elapsed / SLIDE_DURATION, 1);
            setProgress(pct);
            if (pct < 1) requestAnimationFrame(frame);
        };
        const id = requestAnimationFrame(frame);
        return () => cancelAnimationFrame(id);
    }, [current]);

    const slide = slides[current];
    const Icon = slide.icon;

    // Generate particle positions (stable per slide)
    const particleConfigs = slide.particles.flatMap((color, i) => [
        { color, delay: i * 0.4, size: 4, x: 30 + i * 10, y: 55, duration: 3 + i * 0.3 },
        { color, delay: i * 0.4 + 1.5, size: 3, x: 35 + i * 8, y: 50, duration: 3.5 + i * 0.2 },
        { color, delay: i * 0.6 + 0.5, size: 5, x: 45 + i * 5, y: 58, duration: 2.8 + i * 0.4 }
    ]);

    return (
        <div className="relative w-full h-full flex flex-col items-center justify-center px-12 py-10 overflow-hidden bg-gradient-to-br from-gray-950 via-gray-900 to-black">
            {/* Animated background glow that moves with slide */}
            <motion.div
                key={`glow-${current}`}
                className="absolute inset-0 pointer-events-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                style={{
                    background: `radial-gradient(ellipse at 40% 40%, ${slide.glow}, transparent 60%)`
                }}
            />

            {/* Secondary glow bottom-right */}
            <motion.div
                key={`glow2-${current}`}
                className="absolute inset-0 pointer-events-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.5 }}
                transition={{ duration: 1.5 }}
                style={{
                    background: `radial-gradient(ellipse at 70% 80%, ${slide.glow}, transparent 50%)`
                }}
            />

            {/* Floating particles */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={`particles-${current}`}
                    className="absolute inset-0 pointer-events-none"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {particleConfigs.map((p, i) => (
                        <FloatingParticle key={i} {...p} />
                    ))}
                </motion.div>
            </AnimatePresence>

            {/* Main slide content */}
            <div className="relative flex-1 flex flex-col items-center justify-center w-full max-w-lg">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={current}
                        className="flex flex-col items-center text-center"
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                    >
                        {/* Icon with orbit rings */}
                        <motion.div
                            className="relative mb-10"
                            variants={{
                                hidden: { opacity: 0, scale: 0.5 },
                                visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: 'easeOut' } },
                                exit: { opacity: 0, scale: 0.8, transition: { duration: 0.3 } }
                            }}
                        >
                            {/* Orbit rings */}
                            <OrbitRing size={140} duration={8} delay={0} color={slide.accent} />
                            <OrbitRing size={180} duration={12} delay={0.5} color={slide.accent} dashed />
                            <OrbitRing size={220} duration={16} delay={1} color={slide.accent} />

                            {/* Pulsing glow behind icon */}
                            <motion.div
                                className="absolute inset-0 m-auto w-24 h-24 rounded-2xl"
                                style={{ backgroundColor: slide.accent }}
                                animate={{ opacity: [0.1, 0.25, 0.1], scale: [1, 1.3, 1] }}
                                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                            />

                            {/* Icon box */}
                            <motion.div
                                className={`relative w-24 h-24 rounded-2xl bg-gradient-to-br ${slide.gradient} flex items-center justify-center`}
                                style={{ boxShadow: `0 0 40px ${slide.glow}, 0 8px 32px rgba(0,0,0,0.4)` }}
                                whileHover={{ scale: 1.05, rotate: 5 }}
                            >
                                <Icon className="w-12 h-12 text-black" strokeWidth={1.5} />
                            </motion.div>
                        </motion.div>

                        {/* Headline with stagger */}
                        <motion.h2
                            className="text-3xl lg:text-4xl font-bold text-white mb-4"
                            variants={{
                                hidden: { opacity: 0, y: 20 },
                                visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.2 } },
                                exit: { opacity: 0, y: -15, transition: { duration: 0.25 } }
                            }}
                        >
                            {slide.headline}
                        </motion.h2>

                        {/* Accent line */}
                        <motion.div
                            className="h-0.5 rounded-full mb-6"
                            style={{ backgroundColor: slide.accent }}
                            variants={{
                                hidden: { width: 0, opacity: 0 },
                                visible: { width: 60, opacity: 0.6, transition: { duration: 0.5, delay: 0.35 } },
                                exit: { width: 0, opacity: 0, transition: { duration: 0.2 } }
                            }}
                        />

                        {/* Description */}
                        <motion.p
                            className="text-gray-400 text-lg leading-relaxed max-w-md"
                            variants={{
                                hidden: { opacity: 0, y: 15 },
                                visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.4 } },
                                exit: { opacity: 0, y: -10, transition: { duration: 0.2 } }
                            }}
                        >
                            {slide.description}
                        </motion.p>
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Dot indicators with progress */}
            <div className="relative flex items-center justify-center gap-3 mb-8">
                {slides.map((s, index) => (
                    <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className="relative h-2 rounded-full overflow-hidden transition-all duration-500"
                        style={{
                            width: index === current ? 36 : 10,
                            backgroundColor: index === current ? 'transparent' : '#374151'
                        }}
                        aria-label={`Go to slide ${index + 1}`}
                    >
                        {index === current ? (
                            <>
                                {/* Track */}
                                <div className="absolute inset-0 rounded-full bg-gray-700" />
                                {/* Fill */}
                                <motion.div
                                    className="absolute inset-0 rounded-full"
                                    style={{
                                        backgroundColor: s.accent,
                                        transformOrigin: 'left',
                                        scaleX: progress
                                    }}
                                />
                            </>
                        ) : (
                            <motion.div
                                className="absolute inset-0 rounded-full"
                                whileHover={{ backgroundColor: '#6b7280' }}
                            />
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
};

export default FeatureShowcase;
